import { buildProps } from '@lit-element/utils'
import type { ExtractPropTypes, PropType } from 'vue'
import type codeEditor from './codeEditor.vue'

export const codeEditorProps = buildProps({
  value: {
    type: String,
    default: '',
    // 当前编辑器中的代码内容
  },
  language: {
    type: String,
    default: 'javascript',
    // 编辑器的语言模式，例如 'javascript', 'typescript', 'html' 等
  },
  theme: {
    type: String,
    default: 'vs-light',
    // 编辑器的主题，例如 'vs', 'vs-dark', 'hc-black' 等
  },
  // 为只读时编辑器内的提示词
  readOnlyMessage: {
    type: Object,
    default: () => ({
      value: '不可以修改哦',
      supportThemeIcons: true,
      supportHtml: true,
    }),
    // 只读模式下的提示信息，包含 value（提示文本）、supportThemeIcons（是否支持主题图标）、supportHtml（是否支持HTML）
  },
  // 代码折叠
  folding: {
    type: Boolean,
    default: true,
    // 是否启用代码折叠功能
  },
  // 是否只读内容不可编辑
  readOnly: {
    type: Boolean,
    default: false,
    // 是否将编辑器设置为只读模式
  },
  automaticLayout: {
    type: Boolean,
    default: true,
    // 是否自动调整布局以适应容器大小变化
  },
  cursorBlinking: {
    type: String as PropType<'blink' | 'smooth' | 'phase' | 'expand' | 'solid'>,
    default: 'blink',
    // 光标的闪烁效果
  },
  cursorWidth: {
    type: Number,
    default: 2,
    // 光标的宽度
  },
  lineNumbers: {
    type: String as PropType<'on' | 'off' | 'relative' | 'interval'>,
    default: 'on',
    // 行号的显示方式
  },
  roundedSelection: {
    type: Boolean,
    default: false,
    // 是否启用圆角选择
  },
  scrollBeyondLastLine: {
    type: Boolean,
    default: true,
    // 是否允许滚动超过最后一行
  },
  style: {
    type: Object,
    default: () => ({
      width: '100%',
      height: '400px',
    }),
  },
  autoClosingBrackets: {
    type: String as PropType<
      'always' | 'languageDefined' | 'beforeWhitespace' | 'never'
    >,
    default: 'languageDefined',
    // 自动闭合括号的行为
  },
  formatOnType: {
    type: Boolean,
    default: true,
    // 是否在输入时自动格式化代码
  },
  formatOnPaste: {
    type: Boolean,
    default: true,
    // 是否在粘贴时自动格式化代码
  },
  // comments: {
  //   type: Array as PropType<Comment[]>,
  //   default: () => [],
  //   // 代码中的注释信息
  // },
  selectionHighlight: {
    type: Boolean,
    default: true,
    // 是否高亮显示选中的文本
  },
  matchBrackets: {
    type: Boolean,
    default: true,
    // 是否高亮匹配的括号
  },
  fontWeight: {
    type: String,
    default: 'normal',
    // 字体的粗细
  },
  fontSize: {
    type: Number,
    default: 14,
    // 字体的大小
  },
  lineHeight: {
    type: Number,
    default: 20,
    // 行高
  },
  lineDecorationsWidth: {
    type: Number,
    default: 10,
    // 行装饰的宽度
  },
  lineNumbersMinChars: {
    type: Number,
    default: 3,
    // 行号的最小宽度
  },
  letterSpacing: {
    type: Number,
    default: 0,
    // 字符间距
  },
  showUnused: {
    type: Boolean,
    default: true,
    // 是否显示未使用的代码
  },
} as const)

export type CodeEditorProps = ExtractPropTypes<typeof codeEditorProps>
export type CodeEditorInstance = InstanceType<typeof codeEditor>
